import React, { useState, useEffect } from 'react';
import type { ValidationError } from '@/utils/validation/types';
import { useListState } from '@mantine/hooks';
import { MultipliersConfigForm } from '@/components/config_forms/multipliers/MultipliersConfigForm';
import { type RateMultiplier } from '@/types';
import type { OvertimeConcurrencyStrategy, DayRateCalculationBasis } from '@/components/config_forms/multipliers/MultipliersConfigForm';
import { toast } from '@/components/ui/sonner';
import type { ClientFormData } from '../new_client/utilities/formDataUtils';
import { getDefaultNewMultiplier, getSuggestedDayRateThreshold, getSuggestedWeeklyThreshold } from '@/components/config_forms/multipliers/multiplierUtils';

interface ClientMultipliersConfigProps {
  formData: ClientFormData;
  handleChange: (field: keyof ClientFormData, value: ClientFormData[keyof ClientFormData]) => void;
  handleSwitchChange: (field: keyof ClientFormData, checked: boolean) => void;
  onFieldBlur?: (field: keyof ClientFormData) => void;
  isLoading?: boolean;
  errors?: ValidationError[];
  touchedFields?: Set<string>;
  isCustomized?: boolean;
  onRevert?: () => void;
  noCard?: boolean;
  onValidityChange?: (isValid: boolean) => void;
  userDefaultMultipliers?: RateMultiplier[];
}

export const ClientMultipliersConfig: React.FC<ClientMultipliersConfigProps> = ({
  formData,
  handleChange,
  handleSwitchChange,
  onFieldBlur,
  isLoading,
  errors = [],
  touchedFields = new Set(),
  isCustomized,
  onRevert,
  noCard,
  onValidityChange,
  userDefaultMultipliers,
}) => {
  const { rate_multipliers = [], minimum_billable_hours, week_start, overtime_concurrency_strategy } = formData;
  
  const [localMultipliers, localMultipliersHandlers] = useListState<RateMultiplier>(rate_multipliers || []);

  useEffect(() => {
    localMultipliersHandlers.setState(rate_multipliers || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- localMultipliersHandlers is a Mantine useListState handler object that changes identity each render; only sync when source data changes
  }, [rate_multipliers]);

  useEffect(() => {
    onValidityChange?.(true); // Always valid since inline editing handles validation
  }, [onValidityChange]);


  // Derive billing structure from roles
  const billingStructure = formData.roles?.every(r => r.billingStructure === 'day_rate') ? 'day_rate'
    : formData.roles?.every(r => r.billingStructure === 'hourly') ? 'hourly'
    : null;

  const onMultipliersChange = (newMultipliers: RateMultiplier[]) => {
    handleChange('rate_multipliers', newMultipliers);
  };

  const handleRemove = (index: number) => {
    const clientNamePart = formData.name?.trim() ? ` for ${formData.name.trim()}` : '';
    const multiplierToRemove = localMultipliers[index];
    toast.success(`Multiplier "${multiplierToRemove.name}" removed${clientNamePart}.`);
    const updatedMultipliers = localMultipliers.filter((_, i) => i !== index);
    localMultipliersHandlers.setState(updatedMultipliers);
    onMultipliersChange(updatedMultipliers);
  };
  
  return (
    <MultipliersConfigForm
      title="Overtime Multipliers"
      description="Override your default rate multipliers for this client."
      rateMultipliers={localMultipliers}
      onMultipliersChange={onMultipliersChange}
      billingStructure={billingStructure}
      minimumBillableHours={minimum_billable_hours ?? null}
      onRemove={handleRemove}
      // Work week props
      weekStart={week_start}
      onWeekStartChange={(value) => {
        handleChange('week_start', value);
        onFieldBlur?.('week_start');
      }}
      weekStartError={errors.find(e => e.field === 'week_start')?.message}
      isCustomized={isCustomized}
      onRevert={onRevert}
      noCard={noCard}
      context="client"
      overtimeConcurrencyStrategy={overtime_concurrency_strategy as OvertimeConcurrencyStrategy | null}
      onOvertimeConcurrencyStrategyChange={(value) => handleChange('overtime_concurrency_strategy', value)}
      perWeekDayRateCalculationBasis={formData.per_week_day_rate_calculation_basis as DayRateCalculationBasis | null}
      onPerWeekDayRateCalculationBasisChange={(value) => handleChange('per_week_day_rate_calculation_basis', value)}
      parentState={formData}
    />
  );
};
